import { getProp } from "../ParsedSummary";

/**
 * The " (Q3)" that follows a speaker's name, or nothing.
 *
 * The two sources disagree about whether the Q is theirs to write: the model
 * emits "Q3" while the tagged-quote path supplies a bare 3. Prefixing either way
 * gave "(QQ3)" on the model's quotes and "(Q3)" on the tagger's, in the same
 * report. The prefix is added only where it is missing.
 *
 * @param {string|number|null|undefined} qNum The question, however it arrived.
 * @returns {string} " (Q3)", or "" when there is no question to name.
 */
export const questionRef = (qNum) => {
  const bare = String(qNum ?? '').trim().replace(/^q+\s*/i, '');
  return bare ? ` (Q${bare})` : '';
};

// The section is "Top Collaboration Quotes" with no Highlights sub-header, and
// it is dropped entirely when it has nothing to show, rather than printing
// "No Quotes". Negative-climate quotes are no longer listed under it: Critical
// Incidents is where a teacher is told about a quote that needs them.
const deterministicQuotes = (summary, sections) => {
  const quotesData = getProp(summary, ['quote', 'showcase']);
  // v9 flattened the two-branch object into a single topCollaborationQuotes
  // array; v1/v3 still nests its picks under a highlights wrapper.
  const highlightQuotes = Array.isArray(quotesData)
    ? quotesData
    : getProp(getProp(quotesData, ['highlight']), 'quote');

  if (highlightQuotes && highlightQuotes.length > 0) {
    sections.push({ type: 'title-main', content: 'Top Collaboration Quotes' });

    highlightQuotes.forEach((quoteItem) => {
      const quoteRaw = getProp(quoteItem, 'quote');
      const quoteText = typeof quoteRaw === 'string' ? quoteRaw : null;
      const studentName = getProp(quoteItem, ['student', 'name']);
      const qNum = getProp(quoteItem, ['qnum', 'question']);

      if (quoteText) {
        sections.push({
          type: 'ordered-list',
          label: `${studentName || 'Unknown'}${questionRef(qNum)}:`,
          content: `"${quoteText}"`,
        });
      }
    });
  }
};

export const quotes = (summary, sections, deterministic = {}) => {
  if (deterministic.enabled) {
    deterministicQuotes(summary, sections);
    return;
  }

  const quotes = getProp(summary, ['quote', 'showcase']);
  if (quotes) {
    if (Array.isArray(quotes) && quotes.length > 0) {
      sections.push({
        type: 'title',
        content: 'Showcase Quotes'
      });

      quotes.forEach((quoteItem) => {
        const quoteRaw = getProp(quoteItem, 'quote');
        const quoteText = typeof quoteRaw === 'string' ? quoteRaw : null;
        const studentName = getProp(quoteItem, ['student', 'name']);
        const significance = getProp(quoteItem, 'significance');
        const confidence = getProp(quoteItem, 'confidence');

        if (quoteText) {
          sections.push({
            type: 'text',
            label: `${studentName || 'Unknown'}:`,
            content: `"${quoteText}"`
          });
        }
        if (significance && typeof significance === 'string') {
          sections.push({
            type: 'text',
            content: significance
          });
        }
        if (confidence && typeof confidence === 'string') {
          sections.push({
            type: 'text',
            content: confidence
          });
        }
      });
    } else {
      const title = getProp(quotes, 'title');
      sections.push({
        type: 'title-main',
        content: title || 'Top Quotes'
      });

      const highlights = getProp(quotes, ['highlight']);
      const highlightQuotes = getProp(highlights, 'quote');
      sections.push({
        type: 'subtitle',
        content: getProp(highlights, 'title') || 'Highlights'
      });
      if (highlightQuotes && highlightQuotes.length > 0) {
        highlightQuotes.forEach((quoteItem) => {
          const quoteRaw = getProp(quoteItem, 'quote');
          const quoteText = typeof quoteRaw === 'string' ? quoteRaw : null;
          const studentName = getProp(quoteItem, ['student', 'name']);
          const qNum = getProp(quoteItem, ['qnum', 'question']);

          if (quoteText) {
            sections.push({
              type: 'ordered-list',
              label: `${studentName || 'Unknown'}${questionRef(qNum)}:`,
              content: `"${quoteText}"`
            });
          }
        });
      } else {
        sections.push({
          type: 'text',
          content: `No Quotes`
        });
      }

      const issues = getProp(quotes, 'issue');
      const issueQuotes = getProp(issues, 'quote');
      sections.push({
        type: 'subtitle',
        content: getProp(issues, 'title') || 'Issues'
      });

      if (issueQuotes && issueQuotes.length > 0) {
        issueQuotes.forEach((quoteItem) => {
          const quoteRaw = getProp(quoteItem, 'quote');
          const quoteText = typeof quoteRaw === 'string' ? quoteRaw : null;
          const studentName = getProp(quoteItem, ['student', 'name']);
          const qNum = getProp(quoteItem, ['qnum', 'question']);

          if (quoteText) {
            sections.push({
              type: 'ordered-list',
              label: `${studentName || 'Unknown'}${questionRef(qNum)}:`,
              content: `"${quoteText}"`
            });
          }
        });
      } else {
        sections.push({
          type: 'text',
          content: `No Quotes`
        });
      }
    }
  }
}
